import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";

export function Card({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "bg-surface border-hairline shadow-card rounded-(--radius-card) border",
        className,
      )}
      {...props}
    />
  );
}

/**
 * Card header. `action` sits opposite the title rather than below it, which is
 * what keeps a dense admin panel from turning into a stack of centred blocks.
 */
export function CardHeader({
  eyebrow,
  title,
  description,
  action,
  className,
}: {
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "border-hairline flex items-start justify-between gap-4 border-b px-5 py-4",
        className,
      )}
    >
      <div className="min-w-0">
        {eyebrow && <p className="eyebrow mb-1">{eyebrow}</p>}
        <h2 className="text-lg leading-tight font-bold">{title}</h2>
        {description && (
          <p className="text-content-muted mt-1 text-sm">{description}</p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("p-5", className)} {...props} />;
}

/**
 * A band inside a Card, headed like a CardHeader but without a border of its
 * own.
 *
 * Screens that stacked five separate cards down a column use one card with
 * five of these instead. Five borders read as five unrelated widgets, and the
 * gaps between them were the main reason a page felt like scattered boxes with
 * holes in it.
 *
 * The top divider is switched off by the caller (`className="border-t-0"`)
 * rather than with a `first:` variant: sections are frequently split across two
 * column wrappers, and `first:` matches the first child of *each* wrapper,
 * which silently drops the rule between stacked sections on mobile.
 */
export function CardSection({
  eyebrow,
  title,
  description,
  action,
  children,
  className,
}: {
  eyebrow?: string;
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("border-hairline border-t px-5 py-4", className)}>
      {(eyebrow || title || action) && (
        <div className="mb-2.5 flex items-start justify-between gap-3">
          <div className="min-w-0">
            {eyebrow && <p className="eyebrow mb-0.5">{eyebrow}</p>}
            {title && <h2 className="text-base leading-tight font-bold">{title}</h2>}
            {description && <p className="text-content-muted mt-1 text-sm">{description}</p>}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}
      {children}
    </section>
  );
}
